import React from "react";

/**
 * Material UI
 */
import { Box, Typography, Icon, Button, Divider, makeStyles } from '@material-ui/core';
import { SelectionActionButton } from './VoiceButtons';

export const DictionaryButton = (props) => {
    const { onClick } = props;
    return (
        <SelectionActionButton
            title="Chinese dictionary"
            icon="language"
            label="中"
            onClick={onClick}
        />
    );
};

export const AiDictionaryButton = (props) => {
    const { onClick, loading, disabled } = props;
    return (
        <SelectionActionButton
            title="AI Dictionary (Chinese & English)"
            icon="auto_awesome"
            label="AI"
            onClick={onClick}
            loading={loading}
            disabled={disabled}
        />
    );
};

/**
 * Floating dictionary explanations panel.
 * Opaque and high-contrast so content stays readable over the paper.
 */
export const DictionaryMask = (props) => {
    const { explanations = [], clearExplanations, removeExplanation } = props;
    const classes = makeStyles((theme) => ({
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.5),
            width: '100%',
            maxWidth: 560,
            maxHeight: 'min(55vh, 420px)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            [theme.breakpoints.down('sm')]: {
                maxWidth: '100%',
                maxHeight: 'min(42vh, 340px)',
            },
        },
        panel: {
            position: 'relative',
            textAlign: 'left',
            backgroundColor: '#ffffff',
            borderRadius: theme.spacing(1.5),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
            padding: theme.spacing(1.5, 1.75, 1.75),
        },
        header: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        wordTitle: {
            fontWeight: 800,
            fontSize: '1.15rem',
            lineHeight: 1.3,
            color: theme.palette.text.primary,
            paddingRight: theme.spacing(1),
            wordBreak: 'break-word',
        },
        sourceChip: {
            display: 'inline-block',
            marginTop: theme.spacing(0.5),
            padding: theme.spacing(0.15, 0.75),
            borderRadius: theme.spacing(0.75),
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            fontSize: '0.7rem',
            fontWeight: 700,
        },
        headerActions: {
            display: 'flex',
            flexShrink: 0,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: theme.spacing(0.75),
        },
        actionButton: {
            minWidth: 48,
            minHeight: 44,
            padding: theme.spacing(0.75, 1.5),
            borderRadius: theme.spacing(1),
            textTransform: 'none',
            fontWeight: 700,
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
        },
        closeOneButton: {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
                backgroundColor: theme.palette.grey[200],
            },
        },
        closeAllButton: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
        sectionLabel: {
            fontWeight: 800,
            color: theme.palette.primary.main,
            letterSpacing: 0.3,
            marginBottom: theme.spacing(0.5),
            display: 'block',
        },
        explanationText: {
            whiteSpace: 'pre-wrap',
            color: theme.palette.text.primary,
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        sectionBlock: {
            marginTop: theme.spacing(1),
        },
    }))();

    if (!explanations.length) {
        return null;
    }

    const renderExplanationBody = (explanation) => {
        const hasBilingual = Boolean(explanation?.chineseExplanation || explanation?.englishExplanation);

        if (hasBilingual) {
            return (
                <React.Fragment>
                    {Boolean(explanation?.chineseExplanation) &&
                        <Box className={classes.sectionBlock}>
                            <Typography className={classes.sectionLabel} variant="caption">中文</Typography>
                            <Typography className={classes.explanationText}>{explanation.chineseExplanation}</Typography>
                        </Box>}
                    {Boolean(explanation?.chineseExplanation) && Boolean(explanation?.englishExplanation) &&
                        <Box my={1}><Divider /></Box>}
                    {Boolean(explanation?.englishExplanation) &&
                        <Box className={classes.sectionBlock}>
                            <Typography className={classes.sectionLabel} variant="caption">English</Typography>
                            <Typography className={classes.explanationText}>{explanation.englishExplanation}</Typography>
                        </Box>}
                </React.Fragment>
            );
        }

        return (
            <Typography className={classes.explanationText}>{explanation?.explanation}</Typography>
        );
    };

    return (
        <Box className={classes.root}>
            {explanations.map((explanation, eIndex) =>
                <Box key={`${explanation?.word || 'entry'}-${eIndex}`} className={classes.panel}>
                    <Box className={classes.header}>
                        <Box>
                            <Typography className={classes.wordTitle}>{explanation?.word}</Typography>
                            <Typography className={classes.sourceChip} component="span">
                                {explanation?.source === 'ai' ? 'AI Dictionary' : 'Chinese Dictionary'}
                            </Typography>
                        </Box>
                        <Box className={classes.headerActions}>
                            <Button
                                className={`${classes.actionButton} ${classes.closeOneButton}`}
                                startIcon={<Icon>close</Icon>}
                                onClick={() => removeExplanation(explanation?.word)}
                                aria-label="Close this explanation"
                            >
                                Close
                            </Button>
                            {Boolean(eIndex === 0) && explanations.length > 1 &&
                                <Button
                                    className={`${classes.actionButton} ${classes.closeAllButton}`}
                                    startIcon={<Icon>clear_all</Icon>}
                                    onClick={clearExplanations}
                                    aria-label="Close all explanations"
                                >
                                    Close all
                                </Button>}
                        </Box>
                    </Box>
                    {renderExplanationBody(explanation)}
                </Box>
            )}
        </Box>
    );
};
